import React, { useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

type FaqEntry = {
  question: string;
  answer: string | string[] | React.ReactNode;
};

const FAQ_ENTRIES: FaqEntry[] = [
  {
    question: "What is Freshpaint AI?",
    answer: [
      "Freshpaint AI refers to AI-powered features in the Freshpaint platform that help customers analyze data, generate insights, and identify recommendations from data made available through Freshpaint.",
      "Freshpaint AI currently includes features such as Freshpaint AI Chat and Recommendation Cards.",
    ],
  },
  {
    question:
      "What can Freshpaint AI access, and what are its current limitations?",
    answer: [
      "Freshpaint AI Chat has access to the event data, funnel performance, audiences, and analytics available within your Freshpaint account. It can analyze conversion trends, summarize funnel performance, surface drop-off patterns, and generate recommendations based on your actual tracking data.",
      "Freshpaint AI is purpose-built for analytics and CRO workflows. It is not a general-purpose assistant and does not have access to data outside of what is available through your Freshpaint account. It cannot modify your tracking configuration, destination settings, or send data on your behalf.",
      "We are actively working to expand Freshpaint AI's capabilities. If there is a specific feature or data source you would like Freshpaint AI to support, reach out to your Account Manager to share your feedback.",
    ],
  },
  {
    question: "How does Freshpaint AI work?",
    answer: [
      "Freshpaint AI uses large language models (LLMs) and related infrastructure to generate customer-specific insights and outputs based on data available through the Freshpaint platform.",
      "Freshpaint AI Chat is currently supported by Anthropic Claude. Recommendation Cards are processed using Snowflake Cortex and Anthropic Claude models.",
    ],
  },
  {
    question: "Who can enable Freshpaint AI?",
    answer: [
      "Freshpaint AI is optional and can only be enabled by Freshpaint account administrators.",
      "By enabling Freshpaint AI, an account administrator confirms that they are authorized by their organization to enable the feature and acknowledges that data may be processed to provide Freshpaint AI functionality.",
      "If Freshpaint AI is not enabled, all AI-powered features remain disabled for the account.",
    ],
  },
  {
    question: "Which providers support Freshpaint AI?",
    answer: [
      "Freshpaint AI is supported by third-party AI and infrastructure providers, including Anthropic and Snowflake.",
      "Freshpaint AI Chat is currently supported by Anthropic Claude. Recommendation Cards are processed using Snowflake Cortex and Anthropic Claude models.",
    ],
  },
  {
    question: "Does Freshpaint have agreements in place with its AI providers?",
    answer:
      "Yes. Freshpaint maintains appropriate contractual terms with its AI providers, including data protection terms and Business Associate Agreements with both Anthropic and Snowflake where applicable, to support the handling of protected data.",
  },
  {
    question: "Is customer data used to train AI models?",
    answer:
      "No. Customer data submitted through Freshpaint AI features is not used by Freshpaint or its AI subprocessors to train underlying AI models.",
  },
  {
    question: "Where is customer data processed?",
    answer: [
      "Freshpaint AI processing occurs within Freshpaint-managed environments and all data is resident in the United States.",
      "For Freshpaint AI Chat, data submitted through the feature is processed by Anthropic to provide customer-specific functionality and outputs.",
      "For Recommendation Cards, processing occurs using Snowflake Cortex and Anthropic Claude models.",
    ],
  },
  {
    question:
      "Does customer data leave Snowflake for all Freshpaint AI features?",
    answer: [
      "No. The data flow depends on the feature.",
      "For Recommendation Cards, processing occurs using Snowflake Cortex and Anthropic Claude models.",
      "For Freshpaint AI Chat, data submitted through the feature is processed by Anthropic\u2019s Claude model.",
    ],
  },
  {
    question: "Does Freshpaint AI expose my data to other customers?",
    answer: [
      "No. Customer data is not shared with other Freshpaint customers as part of Freshpaint AI.",
      "Freshpaint AI outputs are generated using the customer\u2019s own permitted data and applicable account context.",
    ],
  },
  {
    question: "Is customer data combined with other customers\u2019 data?",
    answer: [
      "No. Customer data is not combined with or associated with other Freshpaint customers\u2019 data to provide Freshpaint AI functionality.",
      "Freshpaint AI is designed to generate customer-specific outputs based on the customer\u2019s own permitted data and applicable context.",
    ],
  },
  {
    question: "Is the Freshpaint AI chat feature a general-purpose chatbot?",
    answer: [
      "No. Freshpaint AI is designed as a purpose-built Freshpaint product feature. It is not intended to be an unrestricted general-purpose chatbot.",
      "Freshpaint AI is designed to generate outputs based on data and context available through Freshpaint, rather than unsupported assumptions or general internet knowledge.",
    ],
  },
  {
    question: "How does Freshpaint reduce hallucinations or incorrect answers?",
    answer: [
      "Freshpaint AI is designed to generate responses based on the customer\u2019s actual Freshpaint data and relevant product context, rather than unsupported assumptions or general internet knowledge.",
      "For Freshpaint AI Chat, the system looks at relevant underlying data before generating insights. Freshpaint also includes safeguards that review responses for consistency and reasonableness before they are shown. These safeguards help reduce unsupported or misleading outputs, but they do not eliminate the need for user review.",
    ],
  },
  {
    question: "Can I see the data behind an AI-generated recommendation?",
    answer: [
      "Where available, Freshpaint aims to connect AI-generated recommendations to the underlying data, metrics, or supporting context.",
      "This helps users review and validate recommendations before acting on them.",
    ],
  },
  {
    question: "Should Freshpaint AI outputs be reviewed?",
    answer: [
      "Yes. Freshpaint AI is designed to support your analysis, and outputs should be reviewed before they are relied on, used, or shared.",
      "Because AI-generated outputs may be incomplete or inaccurate, customers should validate outputs against the underlying data and apply appropriate human review for their use case.",
    ],
  },
  {
    question: "Is it safe to use PHI with Freshpaint AI?",
    answer: [
      "Yes, when you submit PHI to the Freshpaint platform and it is used in accordance with your internal compliance policies, applicable law, and applicable agreements with Freshpaint.",
      "Freshpaint has Business Associate Agreements in place with both Anthropic and Snowflake to support the handling of PHI in connection with Freshpaint AI.",
      "Customers should follow the minimum necessary standard and validate outputs before relying on them.",
    ],
  },
  {
    question: "Who is responsible for appropriate use of Freshpaint AI?",
    answer: [
      "Customers are responsible for ensuring appropriate use of Freshpaint AI, including determining when or if to activate the feature, what inputs are provided to the feature, how the feature is used, and how outputs are reviewed and applied.",
      "Freshpaint AI is intended to assist analysis and does not replace professional judgment or review of the underlying data.",
    ],
  },
  {
    question: "Can Freshpaint AI be disabled?",
    answer: [
      "Yes. Freshpaint AI is optional.",
      "If Freshpaint AI is not enabled, AI-powered features remain off for your account. If your organization no longer wants to use Freshpaint AI, you may elect to disable the feature via the settings tool in Freshpaint or contact your designated Freshpaint Account Manager for assistance.",
    ],
  },
  {
    question:
      "Do customers need to update their existing contract or Terms of Service?",
    answer: (
      <>
        <span className="block">
          {"If you are subject to Freshpaint\u2019s online "}
          <a
            href="https://www.freshpaint.io/terms"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Terms of Service
          </a>
          {
            ", no update to your existing terms is necessary to use Freshpaint AI features once they are enabled. Enabling the feature constitutes acceptance of the applicable terms for its use."
          }
        </span>
        <span className="block">
          If you are unsure whether you are authorized to enable Freshpaint AI,
          or whether your organization&rsquo;s agreement with Freshpaint covers
          use of Freshpaint AI, contact your internal legal, privacy, or
          compliance team or your designated Freshpaint Account Manager for
          assistance.
        </span>
      </>
    ),
  },
  {
    question:
      "How will customers be notified about AI-related subprocessor changes?",
    answer: [
      "Freshpaint maintains an up-to-date list of subprocessors and provides notice of new subprocessors or material changes in accordance with applicable customer agreements.",
      "Customers may raise a good-faith objection within the timeframe specified in their agreement.",
    ],
  },
];

export { FAQ_ENTRIES };

export default function AiChatApprovalFaq() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-6">
      <button
        type="button"
        className="flex cursor-pointer items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        Frequently Asked Questions
      </button>

      {isOpen && (
        <dl className="mt-3 space-y-3">
          {FAQ_ENTRIES.map((entry) => (
            <div key={entry.question}>
              <dt className="text-textHighEmphasis text-sm font-medium">
                {entry.question}
              </dt>
              <dd className="text-textSubtle mt-0.5 space-y-2 text-sm">
                {Array.isArray(entry.answer)
                  ? entry.answer.map((p, i) => (
                      <span key={i} className="block">
                        {p}
                      </span>
                    ))
                  : entry.answer}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
